// Modal to create a "current policies" document request and generate a
// pre-filled mailto: draft. The draft opens in the user's default mail
// client (usually Gmail web if configured, or Outlook). After sending
// they click "Mark as sent" to advance the request to the sent state.

import { useState, useEffect } from 'react';
import { createRequest, updateRequest } from '../../utils/insuranceApi';

export default function RequestDocsModal({ household, onClose, onCreated }) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState(household?.entries?.[0]?.name || '');
  const [dueDays, setDueDays] = useState(14);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdReq, setCreatedReq] = useState(null);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleCreate() {
    setCreating(true);
    setError(null);
    try {
      const dueBy = dueDays
        ? new Date(Date.now() + dueDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : null;
      const req = await createRequest({
        household_id: household.id,
        request_type: 'current_policies',
        recipient_email: recipientEmail || null,
        recipient_name: recipientName || null,
        due_by: dueBy,
        notes: notes || null,
      });
      setCreatedReq(req);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleMarkSent() {
    if (!createdReq) return;
    setMarking(true);
    try {
      await updateRequest(createdReq.id, { status: 'sent' });
      onCreated?.();
      onClose?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setMarking(false);
    }
  }

  // Build the email draft fields. We previously emitted a mailto: URL but
  // that doesn't open Gmail in most browser configs — show the fields
  // in copyable inputs instead so the user can paste them into Gmail.
  function buildEmail() {
    const subject = 'Current insurance docs for your file';
    const firstName = extractFirstName(recipientName);
    const greeting = firstName ? `Hey ${firstName},` : 'Hey,';
    const body = `${greeting}

When you get a chance, could you send over your most recent declarations pages, or a renewal notice if your policy renewed recently, for any of the lines you carry:

  • Auto
  • Home / Condo / Renters
  • Umbrella
  • Life
  • Disability
  • Watercraft / RV
  • Wind / Flood

Most carriers let you pull a current dec page from their member portal in a minute or two. Reply with the PDFs attached and we will take it from there. If a quick walkthrough on where to find them on your carrier's site would help, let us know.

Thanks!`;
    return { to: recipientEmail || '', subject, body };
  }

  // Pull a first name out of either "First Last" or "Last, First" formats.
  // Household records often store names as "Last, First" so we need to
  // handle both, otherwise the greeting reads "Hey Smith,".
  function extractFirstName(fullName) {
    if (!fullName) return '';
    if (fullName.includes(',')) {
      return (fullName.split(',')[1] || '').trim().split(/\s+/)[0] || '';
    }
    return fullName.trim().split(/\s+/)[0] || '';
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-[4px] shadow-[0_8px_30px_rgba(0,0,0,0.15)] w-[560px] max-w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] font-bold text-navy">Request Current Policies</h3>
          <button onClick={onClose} className="text-[20px] text-[#888] hover:text-navy leading-none px-1">×</button>
        </div>

        <p className="text-[11px] text-[#888] mb-4">
          For <span className="font-bold text-navy">{household.name}</span>. We'll create a tracked request and show a pre-filled email draft you can copy into Gmail.
        </p>

        {!createdReq && (
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Recipient Email</label>
              <input
                type="email"
                value={recipientEmail}
                onChange={e => setRecipientEmail(e.target.value)}
                placeholder="client@example.com"
                className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Recipient Name (for greeting)</label>
              <input
                value={recipientName}
                onChange={e => setRecipientName(e.target.value)}
                placeholder="Jane Smith"
                className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Follow up in (days)</label>
              <input
                type="number"
                min={1}
                max={90}
                value={dueDays}
                onChange={e => setDueDays(parseInt(e.target.value) || 0)}
                className="w-24 px-3 py-2 text-[12px] border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue"
              />
              <span className="text-[10px] text-[#888] ml-2">Sets a due date for the request.</span>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Internal Notes (optional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue"
              />
            </div>
            {error && <div className="text-[11px] text-rose">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-3 py-1.5 text-[12px] font-bold text-[#888] bg-gray-100 hover:bg-gray-200 rounded-[4px]">Cancel</button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className={`px-3 py-1.5 text-[12px] font-bold rounded-[4px] ${creating ? 'bg-gray-200 text-gray-400' : 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90'}`}
              >
                {creating ? 'Creating...' : 'Create Request'}
              </button>
            </div>
          </div>
        )}

        {createdReq && (() => {
          const email = buildEmail();
          return (
          <div className="space-y-3">
            <div className="bg-deep-teal/5 border border-deep-teal/20 rounded-[4px] p-3 text-[12px]">
              <div className="font-bold text-deep-teal mb-1">Request created</div>
              <div className="text-[#444]">Status is <span className="font-mono">pending</span>. Copy the fields below into Gmail, send the email, then click Mark as Sent.</div>
            </div>

            <CopyField label="To" value={email.to} />
            <CopyField label="Subject" value={email.subject} />
            <CopyField label="Body" value={email.body} multiline />

            <div className="flex flex-col gap-2 pt-1">
              <button
                onClick={handleMarkSent}
                disabled={marking}
                className={`px-3 py-2 text-[12px] font-bold rounded-[4px] ${marking ? 'bg-gray-200 text-gray-400' : 'bg-deep-teal text-white hover:bg-deep-teal/90'}`}
              >
                {marking ? 'Marking...' : 'Mark as Sent'}
              </button>
              <button onClick={onClose} className="text-[11px] text-[#888] hover:text-navy mt-1">Close (do this later)</button>
            </div>
            {error && <div className="text-[11px] text-rose">{error}</div>}
          </div>
          );
        })()}
      </div>
    </div>
  );
}

// Small inline helper: read-only field with a copy-to-clipboard button.
// Multiline=true renders a <textarea> instead of an <input>.
function CopyField({ label, value, multiline = false }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older browsers / iframes without clipboard access:
      // select the text so the user can hit Ctrl+C.
      const el = document.getElementById(`copyfield-${label}`);
      if (el) { el.select(); document.execCommand && document.execCommand('copy'); }
    }
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] font-bold uppercase tracking-wider text-navy">{label}</label>
        <button
          onClick={handleCopy}
          className={`text-[10px] font-bold px-2 py-0.5 rounded-[3px] ${copied ? 'bg-deep-teal/15 text-deep-teal' : 'bg-bullogic-blue/10 text-bullogic-blue hover:bg-bullogic-blue/20'}`}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {multiline ? (
        <textarea
          id={`copyfield-${label}`}
          readOnly
          value={value || ''}
          rows={Math.min(12, Math.max(4, (value || '').split('\n').length))}
          className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-[4px] bg-bone/30 font-mono leading-snug"
          onFocus={e => e.target.select()}
        />
      ) : (
        <input
          id={`copyfield-${label}`}
          readOnly
          value={value || ''}
          className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-[4px] bg-bone/30"
          onFocus={e => e.target.select()}
        />
      )}
    </div>
  );
}
